import api from './api';

export interface AreaPresetState {
  id: string;
  startTime: string;
  endTime?: string | null;
  /** When set, preset session is expected to end at this time (ISO). */
  scheduledEndTime?: string | null;
  active: boolean;
  userId?: string;
  siteId: string;
  areaId?: string;
  presetId?: string;
  eventType: 'on' | 'off';
  duration?: number;
}

export interface AreaPresetStateWithDetails extends AreaPresetState {
  groupId: string;
  siteName: string | null;
  areaName: string | null;
  presetName: string | null;
  groupName: string | null;
  userName: string | null;
  userEmail: string | null;
}

export interface PaginatedAreaPresetStates {
  data: AreaPresetStateWithDetails[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  totalDuration: number; // Total duration in seconds across all filtered rows
}

export interface UsageDetailsParams {
  siteId?: string;
  areaId?: string;
  presetId?: string;
  groupId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  siteName?: string;
  areaName?: string;
  presetName?: string;
  groupName?: string;
  userName?: string;
}

export interface AggregatedAreaPresetStates {
  period: string;
  totalEvents: number;
  totalDuration: number;
}

function toAggregatedRow(raw: Record<string, unknown>): AggregatedAreaPresetStates {
  const durationRaw = raw.totalDuration ?? raw.total_duration;
  const eventsRaw = raw.totalEvents ?? raw.total_events;
  const duration = typeof durationRaw === 'number' ? durationRaw : Number(durationRaw);
  const events = typeof eventsRaw === 'number' ? eventsRaw : Number(eventsRaw);
  return {
    period: String(raw.period ?? ''),
    totalEvents: Number.isFinite(events) ? events : 0,
    totalDuration: Number.isFinite(duration) ? duration : 0,
  };
}

export const usageApi = {
  getEvents: async (params?: {
    siteId?: string;
    areaId?: string;
    presetId?: string;
    startDate?: string;
    endDate?: string;
    /** Server-side filter: only currently active rows */
    active?: boolean;
  }): Promise<AreaPresetState[]> => {
    const { active, ...rest } = params ?? {};
    const { data } = await api.get('/usage/events', {
      params: { ...rest, ...(active === true ? { active: 'true' } : {}) },
    });
    return data;
  },

  getAggregated: async (params?: {
    siteId?: string;
    areaId?: string;
    presetId?: string;
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }): Promise<AggregatedAreaPresetStates[]> => {
    const { data } = await api.get('/usage/aggregated', { params });
    if (!Array.isArray(data)) return [];
    return data
      .filter((row): row is Record<string, unknown> => row != null && typeof row === 'object')
      .map((row) => toAggregatedRow(row));
  },

  getDetails: async (params?: UsageDetailsParams): Promise<PaginatedAreaPresetStates> => {
    const { data } = await api.get('/usage/details', { params });
    return data;
  },
};

